import { memo } from 'react';
import { Flex, Button } from 'antd';
import clsx from 'clsx';
import { useAppSelector } from 'app/hooks';
import { PLAN_ENTITLEMENT_KEYS } from 'utils/constants';
import usePlanEntitlement from 'hooks/usePlanEntitlement';
import useIsMobile from 'hooks/useIsMobile';
import { useAdaChat } from 'contexts/AdaChatContext';
import If from 'components/If';
import LazySvg from 'components/LazySvg';
import { formatPhoneNumber } from 'utils';
import { getFromBrandConfig } from 'brand-config';
import { selectBrandCustomerServicePhone } from 'services/brand/brandSelectors';
import { selectUserSignInStatus } from 'services/session/sessionSelectors';
import { QaHeader, QaLoginPage } from 'testdata/constants';
import styles from './Menu.module.scss';

type TProps = {
  alignCenter?: boolean;
  className?: string;
};

function CustomerServiceHelp({ alignCenter = false, className = '' }: TProps) {
  const isMobile = useIsMobile();
  const customerServicePhone = useAppSelector(selectBrandCustomerServicePhone);
  const isUserLogin = useAppSelector(selectUserSignInStatus);
  const { isEntitlementFeatureAvailable } = usePlanEntitlement(
    PLAN_ENTITLEMENT_KEYS.customerCareSupportMethods,
    ['phone'],
  );
  const { openAdaChat, isAdaChatAvailable } = useAdaChat();
  const showServiceHelpNumber =
    !!customerServicePhone && (!isUserLogin || isEntitlementFeatureAvailable);

  const wrapperClassName = clsx(styles.helperText, {
    [styles[className]]: className,
    [styles.alignCenter]: alignCenter,
  });
  const hasCustomerServiceSupport = getFromBrandConfig(
    'hasCustomerServiceSupport',
  );

  const handleOpenAdaChat = () => {
    openAdaChat('header');
  };

  return (
    <If condition={hasCustomerServiceSupport}>
      <Flex
        className={wrapperClassName}
        data-qa-selector={QaLoginPage.textCustomerSupport}
      >
        We&apos;re here to help{' '}
        <If condition={isAdaChatAvailable}>
          <Button type='link' onClick={handleOpenAdaChat}>
            Chat with us
          </Button>
          <If condition={showServiceHelpNumber}> or call </If>
        </If>
        <If condition={showServiceHelpNumber}>
          <Flex className='gap8' align='center'>
            <If condition={isMobile}>
              <LazySvg className={styles.callIcon} name='call' />
            </If>
            <Button
              type='link'
              href={`tel:${customerServicePhone}`}
              data-qa-selector={QaHeader.linkCustomerSupportPhone}
            >
              {formatPhoneNumber(customerServicePhone)}
            </Button>
          </Flex>
        </If>
      </Flex>
    </If>
  );
}

export default memo(CustomerServiceHelp);
